import {ChangeDetectorRef, Component, EventEmitter, inject, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatSidenav, MatSidenavContainer, MatSidenavContent} from "@angular/material/sidenav";
import {FlexModule} from "@angular/flex-layout";
import {firstValueFrom, lastValueFrom, Subject, takeUntil} from "rxjs";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatDivider} from "@angular/material/divider";
import {
  AppTableDisplayColumn,
  TableActionItem
} from "projects/nter-ui-kit/src/lib/webkit/components/crud-table/crud-table.model";
import {MatButtonModule, MatIconButton} from "@angular/material/button";
import {MatDialog} from "@angular/material/dialog";
import {
  AddGrantsDialogComponent,
  NewGrantedUser,
  RoleGroupModuleOption
} from "./add-grants-dialog/add-grants-dialog.component";
import {AsyncPipe} from "@angular/common";
import {CrudItemComponent} from "projects/nter-ui-kit/src/lib/webkit/components/crud-item/crud-item.component";
import {CrudTableComponent} from "projects/nter-ui-kit/src/lib/webkit/components/crud-table/crud-table.component";
import {PageHeaderComponent} from "projects/nter-ui-kit/src/lib/webkit/components/page-header/page-header.component";
import {LoadingService} from "projects/nter-ui-kit/src/lib/framework/util/service/loading.service";
import {
  GrantsRoleGroupsService
} from "projects/soko-console-web-v2/src/app/features/settings/users-and-access/grants-role-groups/grants-role-groups.service";
import {SnackbarService} from "projects/nter-ui-kit/src/lib/framework/util/service/snackbar.service";
import {ConsoleContextService} from "projects/soko-console-web-v2/src/app/security/console-context.service";
import {UserAccessService} from "projects/nter-ui-kit/src/lib/framework/security/service/user-access.service";
import {
  GrantForConsoleRequest,
  GroupedPrincipleRole,
  GroupedRole,
  GroupedRoleElevationLevel,
  RegisterUserRequest,
  RoleGrantedUser,
  RoleGroupModule
} from "projects/soko-console-web-v2/src/app/features/settings/users-and-access/grants-role-groups/grants-role-groups.model";
import {RevokeGrant} from "projects/nter-ui-kit/src/lib/framework/security/model/user-access.model";
import {User} from "projects/nter-ui-kit/src/lib/framework/security/model/user.model";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatIconModule} from "@angular/material/icon";
import {MatChipsModule} from "@angular/material/chips";


export const MenuModuleGroupDescriptions: {
  [key: string]: {
    title: string;
    description: string;
    note?: string;
    internalOnly: boolean;
  }
} = {
  'ACCOUNTS': {
    title: 'Accounts',
    description: 'Manage payout groups, run payouts, view transactions and other financial/accounting related tasks',
    note: 'Note, this feature requires the integrated POS module to be enabled',
    internalOnly: false
  },
  'ADMIN': {
    title: 'Admin',
    description: 'Manage users, roles, permissions, and other administrative tasks',
    internalOnly: true
  },
  'DASHBOARDS': {
    title: 'Dashboards',
    description: 'View dashboards and insights related to various features. Access to dashboards are controlled by ' +
      'roles. Adding a user to a role will enable them to see the dashboards section on the corresponding menu group',
    internalOnly: false
  },
  'DISTRICT': {
    title: 'District',
    description: 'Manage districts, locations, and other district related tasks',
    internalOnly: true
  },
  'PIPELINE': {
    title: 'Leads',
    description: 'Features relating to viewing and managing leads, applications, contacts database and the leads ' +
      'form builder',
    internalOnly: false
  },
  'LEASING': {
    title: 'Leasing',
    description: 'Features relating to leasing, including lease management, lease applications, and lease ' +
      'planning',
    internalOnly: false
  },
  'SPACES': {
    title: 'Spaces',
    description: 'Features relating to spaces, including space management and availability',
    internalOnly: false
  },
  'RETAILER_PRODUCTS': {
    title: 'Retailer products',
    description: 'Features relating to retailer products, including importing and viewing products and stock levels.',
    note: 'Note, this feature requires the integrated POS module to be enabled',
    internalOnly: false
  },
  'SETTINGS': {
    title: 'Settings',
    description: 'Manage various settings including general profile info and managing users and access',
    internalOnly: false
  },
}

@Component({
  selector: 'app-grants-role-groups',
  standalone: true,
  imports: [
    CrudItemComponent,
    CrudTableComponent,
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
    FlexModule,
    MatProgressBar,
    MatDivider,
    MatButtonModule,
    AsyncPipe,
    PageHeaderComponent,
    MatToolbarModule,
    MatIconModule,
    MatIconButton,
    MatChipsModule
  ],
  templateUrl: './grants-role-groups.component.html',
  styleUrl: './grants-role-groups.component.scss'
})
export class GrantsRoleGroupsComponent implements OnInit, OnDestroy {
  @Input() selectedOfferingUuid!: string;
  @ViewChild(MatSidenav) sidenavContainer!: MatSidenav;

  private _loader = inject(LoadingService);
  private _service = inject(GrantsRoleGroupsService);
  private _snackbarService = inject(SnackbarService);
  private _clearOnDestroy$ = new Subject<void>();
  private _adminContext = inject(ConsoleContextService);
  private _cdr = inject(ChangeDetectorRef);
  private userAccessService = inject(UserAccessService);

  protected readonly principleRoleGroupModelType = GroupedPrincipleRole;
  protected readonly menuModuleGroupDescriptions = MenuModuleGroupDescriptions;
  protected readonly addGrantModelType = RegisterUserRequest;
  protected readonly memberRolesModelType = GroupedRole;
  protected readonly grantsModelType = RoleGrantedUser;
  protected readonly memberRolesModelTypeDisplayCols: AppTableDisplayColumn[] = [
    AppTableDisplayColumn.INSTANCE({colName: 'roleName', displayLabel: 'Name'}),
    AppTableDisplayColumn.INSTANCE({colName: 'roleDescription', displayLabel: 'Description'}),
  ];
  protected readonly GroupedRoleElevationLevel = GroupedRoleElevationLevel;

  protected loading$ = this._loader.loading$;
  protected pendingChanges: boolean = false;
  protected roleGroupModules: Set<RoleGroupModule> = new Set<RoleGroupModule>();
  protected selectedRoleGroupRow: GroupedPrincipleRole | null = null;
  protected updatedRow: GroupedPrincipleRole | null = null;

  protected addGrantRequest: RegisterUserRequest | null = null;
  protected addGrantRequestUpdated: RegisterUserRequest | null = null;


  protected principleRoleGrantedUsers: RoleGrantedUser[] = [];

  protected addGrantToUserAction = new EventEmitter<any>();
  protected revokeGrantFromUserAction = new EventEmitter<any>();
  protected consolidateUserRolesAction = new EventEmitter<any>();

  protected grantsAdditionalActions: TableActionItem[] = [
    {
      icon: 'remove',
      label: 'Revoke access',
      actionDataSource: 'SelectedRow',
      enabled: "RowSelected",
      includeData: true,
      eventEmitter: this.revokeGrantFromUserAction,
      keepSelectedRowOnClick: false
    },
    {
      icon: 'file_copy_off',
      label: 'Remove duplicate grants',
      actionDataSource: 'SelectedRow',
      enabled: "RowSelected",
      includeData: true,
      eventEmitter: this.consolidateUserRolesAction,
      keepSelectedRowOnClick: true
    },
  ];

  protected isInternalUser = false;
  private _selectedModuleGroup: RoleGroupModule | null = null;

  constructor(public dialog: MatDialog,  protected currentContext: ConsoleContextService) {
    this.addGrantToUserAction.subscribe((event: any) => {
      this.resetAddUserGrantForm();
    });

    this.revokeGrantFromUserAction.subscribe((event: any) => {
      this.handleRevokeUserGrant(event as RoleGrantedUser);
    });

    this.consolidateUserRolesAction.subscribe((event: any) => {
      this.handleConsolidateUserRoles(event as RoleGrantedUser);
    });
  }

  ngOnInit(): void {
    this._service.getAssignableConsoleRoles()
      .pipe(takeUntil(this._clearOnDestroy$))
      .subscribe(data => {
        this.roleGroupModules = data;
      });

  //settings_users_edit_all
    this.isInternalUser = this.currentContext.checkUserFeatures(['settings_internal_admin'], this.selectedOfferingUuid);

    if(this.isInternalUser){
      this.grantsAdditionalActions.push({
        icon: 'add',
        label: 'Add user',
        actionDataSource: 'None',
        enabled: "Always",
        includeData: false,
        eventEmitter: this.addGrantToUserAction,
        keepSelectedRowOnClick: false
      });
      }
  }

  ngOnDestroy(): void {
    this._clearOnDestroy$.next();
  }

  private resetAddUserGrantForm() {
    this.addGrantRequest = new RegisterUserRequest({
      firstname: '',
      lastname: '',
      email: ''
    });
  }

  principleRoleGroupsRowSelected($event: any, moduleGroup: RoleGroupModule | null) {
    const hasData: boolean = $event ?? false;
    this.selectedRoleGroupRow = hasData ? new GroupedPrincipleRole($event) : null;
    this._selectedModuleGroup = hasData ? moduleGroup : null;

    if (hasData) {
      const roleCode = this.selectedRoleGroupRow?.roleCode ?? '';
      firstValueFrom(this._service.getGrantedUsersForRole(roleCode))
        .then(usersArray => {
          this.principleRoleGrantedUsers = usersArray;
          this._cdr.detectChanges();
        }).catch(error => {
        console.error(error);
        this.principleRoleGrantedUsers = [];
      }).finally(() => {
        this._cdr.detectChanges();
        this.sidenavContainer.toggle(hasData);
      });
    } else {
      this.principleRoleGrantedUsers = [];
      this._cdr.detectChanges();
      this.sidenavContainer.toggle(hasData);
    }
  }

  get grantedUsers() {
    return this.principleRoleGrantedUsers;
  }

  handleGracefulClose() {
    if (this.pendingChanges) {
      this._snackbarService.openSnackBar("There are pending changes, please save or discard before closing");
      return;
    }
    this.principleRoleGroupsRowSelected(null, null);
    // this.unselectRow$.next(true);
  }

  protected getModuleDef(code: string | undefined): any {
    if (code && this.menuModuleGroupDescriptions[code]) {
      return this.menuModuleGroupDescriptions[code];
    }

    return null;
  }

  handleAddUserGrantFormSubmission($event: any) {
    if ($event.data) {
      const request = this.buildAddUserGrantRequest(
        this.addGrantRequest!,
        [this.selectedRoleGroupRow!.roleCode!]
      )
      this._grantAccessToUser(request);
    }
  }

  private buildAddUserGrantRequest(addGrantRequest: RegisterUserRequest, roleCodes: string[]): GrantForConsoleRequest {
    return new GrantForConsoleRequest({
      user: {
        firstname: addGrantRequest!.firstname,
        lastname: addGrantRequest!.lastname,
        email: addGrantRequest!.email
      },
      offeringUuid: this.selectedOfferingUuid!,
      principleId: this.selectedOfferingUuid!,
      roleCodes: roleCodes
    });
  }

  private _grantAccessToUser(request: GrantForConsoleRequest) {
    lastValueFrom(this.userAccessService.saveAccessRequest(request as any))
      .then(() => {
        this.principleRoleGrantedUsers.push(new RoleGrantedUser({
          userFirstname: request!.user.firstname,
          userLastname: request!.user!.lastname,
          userEmail: request!.user!.email
        }));

        this.addGrantRequest = null;
        this._snackbarService.openSnackBar("User granted access successfully");
      })
      .catch((error: string) => {
          this._snackbarService.openSnackBar("Error granting user access. Please try again.");
        }
      );
  }

  //todo may not need this
  grantedUserRowSelected($event: any) {
  }

  toggleAddGrantsDialog() {
    let addGrantDialogRef = this.dialog.open(AddGrantsDialogComponent, {
      panelClass: 'dialog-xl',
      maxHeight: 'auto',
      data: {
        roleGroupOptions: this.roleGroupModuleOptions(),
        currentUser: this.toNewGrantedUser(this._adminContext.currentUser)
      }
    })

    addGrantDialogRef.afterClosed().subscribe((response: {
      users: NewGrantedUser[],
      roleCodes: string[]
    }) => {
      console.log(response);
      if (response.users && response.roleCodes) {
        response.users.forEach(user => {
          const request = this.buildAddUserGrantRequest(
            new RegisterUserRequest({
              firstname: user.name,
              lastname: user.surname,
              email: user.email
            }),
            response.roleCodes
          );
          this._grantAccessToUser(request);
        })
      }
    });
  }

  private roleGroupModuleOptions(): RoleGroupModuleOption[] {
    return Array.from(this.roleGroupModules).map(module => {
      return {
        moduleGroupName: module.moduleGroupCode ?? 'Group',
        principleRoles: module.principleRoleGroups!.map(role => {
          return {
            label: role.roleName!,
            code: role.roleCode!
          }
        })
      }
    });
  }

  handleUpdateCanceled($event: boolean) {
    this.addGrantRequest = null;
    this.addGrantRequestUpdated = null;
  }

  private handleRevokeUserGrant(roleGrantedUser: RoleGrantedUser) {
    const request: RevokeGrant = {
      grantId: roleGrantedUser.grantId!,
      userId: roleGrantedUser.userId!,
      offeringUuid: this.selectedOfferingUuid!,
      principleId: roleGrantedUser.grantPrincipleId!,
      roleId: this.selectedRoleGroupRow!.roleId!
    };

    lastValueFrom(this.userAccessService.revokeAccess([request]))
      .then(() => {
          this.principleRoleGrantedUsers = this.principleRoleGrantedUsers.filter(user => user.grantId !== roleGrantedUser.grantId);
          this._snackbarService.openSnackBar("User access revoked successfully");
        }
      ).catch((error: string) => {
      this._snackbarService.openSnackBar("Error revoking user access. Please try again.");
    });
  }

  private handleConsolidateUserRoles(grantedUser: RoleGrantedUser) {
    lastValueFrom(this.userAccessService.reconcileGrants(grantedUser.userId!))
      .then(() => {
        this._snackbarService.openSnackBar("User roles consolidated successfully");
      })
      .catch((error: string) => {
        this._snackbarService.openSnackBar("Error consolidating user roles. Please try again.");
      });
  }

  private toNewGrantedUser(user: User | null): NewGrantedUser {
    return {
      name: user!.firstname,
      surname: user!.lastname,
      email: user!.email
    };
  }

  shouldRenderModuleGroup(moduleGroupCode: string | undefined) : boolean{
    return (this.getModuleDef(moduleGroupCode)?.internalOnly && this.isInternalUser) ||
    this.getModuleDef(moduleGroupCode)?.internalOnly === false
  }

  shouldClearRow(moduleGroup : any){
    return moduleGroup !== this._selectedModuleGroup;
  }
}
